import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import PaginationControls from "../../../components/base/PaginationControls";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useDarkMode } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import Swal from "sweetalert2";
import { useTruckYardStore } from "../../../store/truckYardStore";
import {
  fetchAccessCards,
  type AccessCard,
} from "../../../store/oasis/hikvisionAccessStore";
import { useAllTruckYardVehicles } from "../../../features/truckYard/queries";
import {
  canChangeYardSpecialTags,
  normalizeSpecialTags,
  specialTagErrorKey,
  specialTagOptions,
  specialTagsCollide,
  specialTagsToSubmit,
  type SpecialTagKind,
  type YardSpecialTags,
} from "../specialTags";
import { useUsersStore } from "../../../store/admin/usersStore";
import { useAllDevices } from "../../../features/devices/queries";
import { useIsReseller } from "../../../hooks/useIsReseller";
import useTruckYardRole from "../../../hooks/useTruckYardRole";
import api from "../../../utils/api";
import { APIProvider, Map, Marker, useMap } from "@vis.gl/react-google-maps";
import Circle from "../../../pages/oasis/yards-section/components/Circle";
import LocationSearchInput from "../../../components/LocationSearchInput";
import { getCustomSelectStyles } from "../../../Layouts/admin/shared";
import { TIMEZONE_OPTIONS } from "../../../Layouts/admin/components/sections/trips/types";
import HeaderComponent from "../components/HeaderComponent";
import NoAssignedYardsNotice from "../../../components/base/NoAssignedYardsNotice";
import { WATER_TYPES as YARD_TYPES } from "../components/waterTypes";
import YardDetailsModal from "./YardDetailsModal";
import YardOperationSummary from "./YardOperationSummary";
import {
  useYardMetrics,
  type YardMetricsEntry,
} from "../../../features/truckYard/useYardMetrics";
import { useYardsPoll } from "../../../features/truckYard/useYardsPoll";
import type { ThemeClasses, Yard } from "./types";

// ─── Types ─────────────────────────────────────────────────────────────────--

interface YardFormData {
  name: string;
  location: string;
  radius?: string;
  guardHousePhone?: string;
  timeZone?: string;
  privacyPolicies?: string;
  status?: string;
  type?: string[];
  latitude?: number;
  longitude?: number;
  logo?: string;
  // Users to notify on geofence entry/exit. Persisted to the linked
  // geofence (and mirrored on the yard) by the backend.
  notifiedUsers?: string[];
  // Device IDs tracked by the linked geofence. Managed here and also kept in
  // sync from vehicle assignments.
  devices?: string[];
  // Crossing events to react to (entry/exit) — like the geofences form.
  geofenceActions?: string[];
  // If true, crossing events also send email (not just push) to notifiedUsers.
  sendEmails?: boolean;
  // Los dos tags compartidos del patio. Es un objeto, así que NO pasa por el
  // armado genérico del FormData (ver handleSubmit del modal).
  special_tags?: YardSpecialTags;
}

const EMPTY_FORM: YardFormData = {
  name: "",
  location: "",
  radius: "300",
  guardHousePhone: "",
  timeZone: "America/Mexico_City",
  privacyPolicies: "",
  status: "active",
  type: [],
  latitude: undefined,
  longitude: undefined,
  notifiedUsers: [],
  devices: [],
  geofenceActions: ["entry", "exit"],
  sendEmails: false,
  special_tags: { provider: null, untagged: null },
};

// `/accessCardList` acepta 100 por página como máximo
// (hikvisionAccessSchema.getAccessCardList). El tope de páginas es una red por
// si la paginación responde mal, no un límite esperado: 20 páginas son 2000
// tags, y el único conteo medido que hay en el repo son los 20 de Acuapue
// (yardInspectionsStore.ts, MISSING_EXIT_AFTER_HOURS).
const TAG_CARDS_PAGE_SIZE = 100;
const TAG_CARDS_MAX_PAGES = 20;

// Full timezone list, identical to the geofences/trips form (MX, US, Latam,
// Canada, Europe, UTC). Derived from the shared TIMEZONE_OPTIONS map so the
// patio's linked geofence gets the same IANA values the engine expects.
const TIMEZONES = Object.entries(TIMEZONE_OPTIONS).map(([value, label]) => ({
  value,
  label,
}));

// Radius bounds for the slider — same range as the geofences circular form.
const RADIUS_MIN = 50;
const RADIUS_MAX = 2000;

// ─── Estado administrativo del patio ─────────────────────────────────────────

/**
 * El Activo/Inactivo de `yard.status`, que NO desaparece con #499 pero deja de
 * ser lo único que la tarjeta muestra.
 *
 * Es un campo de configuración: lo escribe un humano en el formulario de alta y
 * dice si el patio está dado de alta como entidad, no si su control de acceso
 * responde. La confusión entre las dos cosas es el defecto de fondo de esta
 * issue — siguió en verde durante los seis días de corte de #490 —, así que
 * ahora lleva el ícono de configuración y una aclaración al pasar el mouse, y
 * convive con el chip de enlace de `YardOperationSummary`, que dice la otra
 * cosa. Un patio "Activo" con el controlador caído es exactamente el estado que
 * antes era invisible: ahora se ven los dos badges a la vez y no se contradicen
 * porque no responden la misma pregunta.
 */
function AdminStatusBadge({ yard }: { yard: Yard }) {
  const { t } = useTranslation("truckYard");
  const active = yard.status === "active";
  return (
    <span
      title={t("yards.card.adminStatusHint")}
      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ${
        active
          ? "bg-green-500/20 text-green-500"
          : "bg-red-500/20 text-red-400"
      }`}
    >
      <i className="ri-settings-3-line text-[11px]"></i>
      {active ? t("yards.card.active") : t("yards.card.inactive")}
    </span>
  );
}

// ─── List Row (card-style dark list as in image) ─────────────────────────────

function YardListRow({
  yard,
  metrics,
  themeClasses,
  onDetails,
  onEdit,
  onDelete,
  canDelete = true,
}: {
  yard: Yard;
  metrics: YardMetricsEntry | undefined;
  themeClasses: ThemeClasses;
  onDetails: (y: Yard) => void;
  onEdit: (y: Yard) => void;
  onDelete: (y: Yard) => void;
  canDelete?: boolean;
}) {
  const { t } = useTranslation("truckYard");
  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-md transition-all duration-200`}
    >
      {/* Mobile: stacked layout */}
      <div className="flex flex-col gap-3 sm:hidden">
        <div className="flex items-center gap-3">
          {yard.logo && yard.logo !== "null" && yard.logo !== "" ? (
            <img
              src={yard.logo}
              alt={yard.name}
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              onError={(e) => {
                // Si la imagen falla al cargar, mostrar el icono por defecto
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          {(!yard.logo || yard.logo === "null" || yard.logo === "") && (
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-500/20">
              <i className="ri-building-4-line text-amber-500 text-lg"></i>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {yard.name}
            </p>
            <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
              {yard.id}
            </p>
          </div>
        </div>
        {/* La operación, en el lugar donde estaban Radio y Tel. de Caseta. */}
        <YardOperationSummary
          health={yard.health}
          metrics={metrics}
          themeClasses={themeClasses}
          compact
        />

        <div className="flex items-center justify-between">
          <AdminStatusBadge yard={yard} />
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDetails(yard)}
              className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title={t("yards.card.viewDetails")}
            >
              <i
                className={`ri-eye-line text-sm ${themeClasses.buttonText}`}
              ></i>
            </button>
            <button
              onClick={() => onEdit(yard)}
              className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title={t("common.edit")}
            >
              <i
                className={`ri-pencil-line text-sm ${themeClasses.buttonText}`}
              ></i>
            </button>
            {canDelete && (
              <button
                onClick={() => onDelete(yard)}
                className="p-1.5 bg-red-500/10 text-red-500 rounded-lg transition-colors hover:bg-red-500/20"
                title={t("common.delete")}
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: horizontal card row matching the image */}
      <div className="hidden sm:flex items-center gap-4">
        {/* Left: Icon + Name */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {yard.logo && yard.logo !== "null" && yard.logo !== "" ? (
            <img
              src={yard.logo}
              alt={yard.name}
              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
              onError={(e) => {
                // Si la imagen falla al cargar, mostrar el icono por defecto
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          {(!yard.logo || yard.logo === "null" || yard.logo === "") && (
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-amber-500/20">
              <i className="ri-building-4-line text-amber-500 text-lg"></i>
            </div>
          )}
          <div className="min-w-0">
            <p
              className={`font-semibold text-sm ${themeClasses.text} truncate`}
            >
              {yard.name}
            </p>
            <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
              {yard.id}
            </p>
          </div>
        </div>

        {/* La operación. Ya no está bajo `hidden md:block` como el radio y el
            teléfono a los que reemplaza: un dato que se oculta en la mitad de
            los anchos no sirve para lo que un operador mira cada mañana. */}
        <div className="min-w-0 flex-1">
          <YardOperationSummary
            health={yard.health}
            metrics={metrics}
            themeClasses={themeClasses}
            compact
          />
        </div>

        {/* Badge + Actions */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <AdminStatusBadge yard={yard} />

          {/* Action buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onDetails(yard)}
              className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title={t("yards.card.viewDetails")}
            >
              <i
                className={`ri-eye-line text-sm ${themeClasses.buttonText}`}
              ></i>
            </button>
            <button
              onClick={() => onEdit(yard)}
              className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
              title={t("common.edit")}
            >
              <i
                className={`ri-pencil-line text-sm ${themeClasses.buttonText}`}
              ></i>
            </button>
            {canDelete && (
              <button
                onClick={() => onDelete(yard)}
                className="p-1.5 bg-red-500/10 text-red-500 rounded-lg transition-colors hover:bg-red-500/20"
                title={t("common.delete")}
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tipo (shared between mobile + desktop) */}
      {yard.type && yard.type.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-start gap-2">
          <i
            className={`ri-drop-line text-xs ${themeClasses.textSecondary} mt-0.5`}
          ></i>
          <div className="flex flex-wrap gap-1">
            {yard.type.map((typeValue: string) => {
              const type = YARD_TYPES.find((yt) => yt.value === typeValue);
              return (
                <span
                  key={typeValue}
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                >
                  {type?.label ?? typeValue}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Grid Card ────────────────────────────────────────────────────────────────

function YardGridCard({
  yard,
  metrics,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  canDelete = true,
}: {
  yard: Yard;
  metrics: YardMetricsEntry | undefined;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (y: Yard) => void;
  onEdit: (y: Yard) => void;
  onDelete: (y: Yard) => void;
  canDelete?: boolean;
}) {
  const { t } = useTranslation("truckYard");
  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-4 hover:shadow-lg transition-all duration-200 flex flex-col gap-3`}
    >
      {/* Top */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          {yard.logo && yard.logo !== "null" && yard.logo !== "" ? (
            <img
              src={yard.logo}
              alt={yard.name}
              className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
              onError={(e) => {
                // Si la imagen falla al cargar, mostrar el icono por defecto
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          {(!yard.logo || yard.logo === "null" || yard.logo === "") && (
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                isDarkMode ? "bg-amber-500/20" : "bg-amber-50"
              }`}
            >
              <i className="ri-building-4-line text-amber-500 text-lg"></i>
            </div>
          )}
          <div>
            <h3
              className={`font-semibold text-sm ${themeClasses.text} line-clamp-2`}
            >
              {yard.name}
            </h3>
            <span className={`text-xs font-mono ${themeClasses.textSecondary}`}>
              {yard.id}
            </span>
          </div>
        </div>
        {/* Era un punto de color pelado, y ése era el problema: un punto sin
            etiqueta se lee como "el patio está bien", cuando lo que decía era
            `yard.status`, el toggle del formulario. Ahora lleva su nombre y su
            ícono de configuración; la conexión del controlador tiene su propio
            chip abajo. */}
        <AdminStatusBadge yard={yard} />
      </div>

      {/* Location */}
      <div
        className={`flex items-start gap-2 text-xs ${themeClasses.textSecondary}`}
      >
        <i className="ri-map-pin-line mt-0.5 flex-shrink-0"></i>
        <span className="line-clamp-2">{yard.location}</span>
      </div>

      {/* Operación: el estado del controlador y lo que pasó hoy en el patio,
          donde antes estaban Radio y Tel. de Caseta. */}
      <div
        className={`${isDarkMode ? "bg-dark-surface" : "bg-gray-50"} rounded-lg p-2.5`}
      >
        <YardOperationSummary
          health={yard.health}
          metrics={metrics}
          themeClasses={themeClasses}
        />
      </div>

      {/* Tipo */}
      {yard.type && yard.type.length > 0 && (
        <div className="flex items-start gap-2">
          <i
            className={`ri-drop-line text-xs ${themeClasses.textSecondary} mt-0.5`}
          ></i>
          <div className="flex flex-wrap gap-1">
            {yard.type.map((typeValue: string) => {
              const type = YARD_TYPES.find((yt) => yt.value === typeValue);
              return (
                <span
                  key={typeValue}
                  className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-cyan-500/15 text-cyan-400 border border-cyan-500/30"
                >
                  {type?.label ?? typeValue}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center gap-2 pt-1 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => onDetails(yard)}
          className="flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors text-center"
          style={{ backgroundColor: brand.primary, color: brand.accent }}
        >
          <i className="ri-eye-line mr-1"></i>
          {t("yards.card.details")}
        </button>
        <button
          onClick={() => onEdit(yard)}
          className={`flex-1 py-1.5 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-xs font-medium transition-colors text-center`}
        >
          <i className="ri-pencil-line mr-1"></i>
          {t("yards.card.edit")}
        </button>
        {canDelete && (
          <button
            onClick={() => onDelete(yard)}
            className="w-auto px-2 py-1.5 bg-red-500/10 text-red-500 border border-red-500/20 rounded-lg text-xs font-medium transition-colors text-center hover:bg-red-500/20"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Map Controller ────────────────────────────────────────────────────────────

function MapController({ center }: { center: { lat: number; lng: number } }) {
  const map = useMap();
  useEffect(() => {
    if (map) map.panTo(center);
  }, [map, center.lat, center.lng]);
  return null;
}

// ─── Form Modal ───────────────────────────────────────────────────────────────

function YardFormModal({
  themeClasses,
  isDarkMode,
  form,
  yardId,
  canEditSpecialTags,
  isEdit,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  form: YardFormData;
  /** Patio que se edita; `null` en el alta. */
  yardId: string | null;
  /** Vigilante, inspector y supervisor ven los tags especiales, no los cambian. */
  canEditSpecialTags: boolean;
  isEdit: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (formData: FormData) => void;
}) {
  const { t } = useTranslation("truckYard");
  const [localForm, setLocalForm] = useState<YardFormData>(form);
  const [errors, setErrors] = useState<
    Partial<Record<keyof YardFormData, string>>
  >({});

  // ── Tags especiales ───────────────────────────────────────────────────────
  // Las tarjetas se piden sólo para quien puede cambiarlos: `/accessCardList`
  // lleva `denyYardOperators`, así que a un vigilante, inspector o supervisor
  // le respondería 403 — y de todos modos su selector va en sólo lectura.
  const [tagCards, setTagCards] = useState<AccessCard[]>([]);
  const [tagCardsState, setTagCardsState] = useState<
    "loading" | "ready" | "error"
  >(canEditSpecialTags ? "loading" : "ready");
  useEffect(() => {
    if (!canEditSpecialTags) return;
    let cancelled = false;
    (async () => {
      try {
        const all: AccessCard[] = [];
        let page = 1;
        let totalPages = 1;
        do {
          const res = await fetchAccessCards({
            type: "tag",
            limit: TAG_CARDS_PAGE_SIZE,
            page,
          });
          all.push(...(res.data || []));
          totalPages = res.pagination?.totalPages ?? 1;
          page += 1;
        } while (page <= totalPages && page <= TAG_CARDS_MAX_PAGES);
        if (cancelled) return;
        setTagCards(all);
        setTagCardsState("ready");
      } catch {
        // Sin la lista el selector no se puede ofrecer: se deja en sólo
        // lectura con el valor guardado, y el resto del patio se guarda igual
        // (un campo que no cambió no viaja).
        if (!cancelled) setTagCardsState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEditSpecialTags]);

  // Unidades `is_provider`: su tag es la única tarjeta ASIGNADA que puede ser
  // tag de proveedores (el esquema anterior). El catálogo ya lo carga el resto
  // de Patios con la misma query, así que acá casi siempre sale de caché.
  useAllTruckYardVehicles();
  const yardVehicles = useTruckYardStore((s) => s.vehicles);
  const providerVehicleIds = useMemo(
    () => new Set(yardVehicles.filter((v) => v.is_provider).map((v) => v.id)),
    [yardVehicles],
  );

  const savedSpecialTags = useMemo(
    () => normalizeSpecialTags(form.special_tags),
    [form.special_tags],
  );
  const specialTags = normalizeSpecialTags(localForm.special_tags);
  const [specialTagsError, setSpecialTagsError] = useState<string | null>(null);
  const setSpecialTag = (kind: SpecialTagKind, value: string) => {
    setLocalForm((prev) => ({
      ...prev,
      special_tags: {
        ...normalizeSpecialTags(prev.special_tags),
        [kind]: value || null,
      },
    }));
    setSpecialTagsError(null);
  };
  const specialTagsLocked =
    !canEditSpecialTags || tagCardsState !== "ready";
  const specialTagSelectors = useMemo(
    () =>
      (
        [
          { kind: "provider", otherKind: "untagged" },
          { kind: "untagged", otherKind: "provider" },
        ] as const
      ).map(({ kind, otherKind }) => ({
        kind,
        options: specialTagOptions({
          kind,
          cards: tagCards,
          yardId,
          current: specialTags[kind],
          other: specialTags[otherKind],
          providerVehicleIds,
        }),
      })),
    [tagCards, yardId, specialTags.provider, specialTags.untagged, providerVehicleIds],
  );

  // Helper to check if logo is valid
  const isValidLogo = (logo: string | undefined | null): boolean => {
    return !!logo && logo !== "null" && logo !== "" && logo.length > 10;
  };

  // Image upload states
  const [imagePreview, setImagePreview] = useState<string | null>(
    isValidLogo(form.logo) ? form.logo! : null,
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDeleted, setImageDeleted] = useState(false);

  // Sync form prop and reset image states when form changes
  useEffect(() => {
    setLocalForm(form);
    setImagePreview(isValidLogo(form.logo) ? form.logo! : null);
    setImageFile(null);
    setImageDeleted(false);
  }, [form]);

  // Google Maps state
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>(
    localForm.latitude && localForm.longitude
      ? { lat: localForm.latitude, lng: localForm.longitude }
      : { lat: 25.6866, lng: -100.3161 }, // Default to Monterrey
  );

  const [mapRadius, setMapRadius] = useState<number>(
    parseInt(localForm.radius || "300") || 300,
  );

  // Users to notify (manual side of the hybrid model). Persisted to the
  // linked geofence's notifiedUsers. Same behavior as the geofences form:
  // resellers get every user (bypass company scope), carriers get only the
  // users of the active company.
  const {
    usersLight,
    fetchUsersLight,
    usersLightLoading: usersLoading,
  } = useUsersStore();
  const { isReseller } = useIsReseller();
  useEffect(() => {
    fetchUsersLight({ allCompanies: isReseller });
  }, [fetchUsersLight, isReseller]);

  const userOptions = useMemo(
    () =>
      usersLight.map((u) => ({
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.email})`,
      })),
    [usersLight],
  );

  // Devices tracked by the linked geofence (same options/labels as the
  // geofences form). The geofence's `devices` array stores these device IDs.
  const devicesQuery = useAllDevices();
  const devices = devicesQuery.data ?? [];
  const devicesLoading = devicesQuery.isLoading;

  const deviceOptions = useMemo(
    () =>
      devices.map((d) => {
        const vehicleName = d.vehicle ? d.vehicle?.name : "";
        const trailerName = d.remolque ? d.remolque?.name : "";
        const nameDevice = vehicleName || trailerName ? d.identifier : d.name;
        return {
          value: d.id,
          label: `${nameDevice}${vehicleName ? ` (Vehículo: ${vehicleName})` : ""}${trailerName ? ` (Remolque: ${trailerName})` : ""}`,
        };
      }),
    [devices],
  );

  const surface = isDarkMode ? "bg-[#2a2a2a]" : "bg-gray-50";
  const cardCls = `w-full px-3 py-2.5 ${surface} border border-transparent rounded-xl ${themeClasses.text} focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500/50 outline-none transition-all text-sm`;
  const labelCls = `text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-1.5 block`;

  const set = (
    field: keyof YardFormData,
    value: string | number | string[] | boolean,
  ) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleRadiusChange = (radius: number) => {
    const rounded = Math.round(radius);
    setMapRadius(rounded);
    set("radius", rounded.toString());
  };

  const handleMarkerDrag = (e: any) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMapCenter({ lat, lng });
      set("latitude", lat);
      set("longitude", lng);
    }
  };

  const handleCenterOnMyLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire({
        icon: "error",
        title: "Geolocalización no soportada",
        text: "Tu navegador no soporta geolocalización",
      });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setMapCenter({ lat, lng });
        set("latitude", lat);
        set("longitude", lng);
        Swal.fire({
          icon: "success",
          title: "Ubicación encontrada",
          text: `Latitud: ${lat.toFixed(6)}, Longitud: ${lng.toFixed(6)}`,
          timer: 2000,
          showConfirmButton: false,
        });
      },
      (error) => {
        console.error("Error getting location:", error);
        Swal.fire({
          icon: "error",
          title: "Error de ubicación",
          text: "No se pudo obtener tu ubicación. Asegúrate de dar permisos de ubicación.",
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageDeleted(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImagePreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDelete = () => {
    setImagePreview(null);
    setImageFile(null);
    setImageDeleted(true);
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof YardFormData, string>> = {};
    if (!localForm.name.trim()) newErrors.name = t("yards.form.errors.nameRequired");
    if (!localForm.location.trim())
      newErrors.location = t("yards.form.errors.locationRequired");
    if (!localForm.guardHousePhone?.trim())
      newErrors.guardHousePhone = t("yards.form.errors.contactRequired");
    if (!localForm.timeZone)
      newErrors.timeZone = t("yards.form.errors.timezoneRequired");
    setErrors(newErrors);
    // El selector ya no ofrece en uno lo elegido en el otro, así que esto sólo
    // salta con datos guardados que chocaban de antes. Se corta acá para no
    // gastar el viaje en un `400 SpecialTagsMustDiffer` seguro.
    const tagsCollide = canEditSpecialTags && specialTagsCollide(specialTags);
    setSpecialTagsError(
      tagsCollide ? t("yards.specialTags.errors.mustDiffer") : null,
    );
    return Object.keys(newErrors).length === 0 && !tagsCollide;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const formData = new FormData();

      // Add form fields
      Object.entries(localForm).forEach(([key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          key !== "logo" &&
          // Es un objeto: por acá saldría como "[object Object]". Va abajo.
          key !== "special_tags"
        ) {
          if (Array.isArray(value)) {
            // Arrays (type, notifiedUsers) are JSON-encoded; the backend
            // parses them back with parseJsonArray.
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      // Los tags especiales viajan como JSON string, igual que los arrays, y
      // sólo si cambiaron: por qué, en specialTagsToSubmit.
      const tagsToSubmit = specialTagsToSubmit({
        canChange: canEditSpecialTags,
        saved: savedSpecialTags,
        current: specialTags,
      });
      if (tagsToSubmit) {
        formData.append("special_tags", JSON.stringify(tagsToSubmit));
      }

      // Add image file if exists
      if (imageFile) {
        formData.append("imageFile", imageFile);
      } else if (imageDeleted) {
        formData.append("logo", "null");
      }

      onSubmit(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative ${themeClasses.cardBg} rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-6 py-2 border-b border-gray-700/30 bg-inherit rounded-t-2xl flex-shrink-0">
          <h2 className={`text-base font-bold ${themeClasses.text}`}>
            {isEdit ? t("yards.form.editTitle") : t("yards.form.newTitle")}
          </h2>
          <button
            onClick={onClose}
            className={`p-1.5 ${themeClasses.buttonHover} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <form
          id="yard-form"
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="px-4 sm:px-10 py-4 space-y-4 flex-1 overflow-y-auto overflow-x-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 min-h-0 flex-1">
              {/* Left column - Inputs */}
              <div className="lg:col-span-2 space-y-4">
                <div>
                  <label className={labelCls}>
                    {t("yards.form.name")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={localForm.name}
                    onChange={(e) => set("name", e.target.value)}
                    className={`${cardCls} ${errors.name ? "border-red-500/60" : ""}`}
                    placeholder={t("yards.form.namePlaceholder")}
                  />
                  {errors.name && (
                    <span className="text-red-400 text-xs mt-1">
                      {errors.name}
                    </span>
                  )}
                </div>

                <div>
                  <label className={labelCls}>{t("yards.form.yardType")}</label>
                  <div className="grid grid-cols-2 gap-2">
                    {YARD_TYPES.map((type) => (
                      <label
                        key={type.value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={
                            localForm.type?.includes(type.value) || false
                          }
                          onChange={(e) => {
                            const currentTypes = localForm.type || [];
                            if (e.target.checked) {
                              set("type", [...currentTypes, type.value]);
                            } else {
                              set(
                                "type",
                                currentTypes.filter((tv) => tv !== type.value),
                              );
                            }
                          }}
                          className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                        />
                        <span className={`text-sm ${themeClasses.text}`}>
                          {type.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>{t("yards.form.logo")}</label>
                  <div className="space-y-2">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Logo preview"
                          className="w-full h-32 object-cover rounded-xl border border-gray-700/30"
                          onError={() => {
                            // Si la imagen falla al cargar, limpiar el preview
                            setImagePreview(null);
                            setImageDeleted(true);
                          }}
                        />
                        <button
                          aria-label={t("general.delete", { ns: "common" })}
                          type="button"
                          onClick={handleImageDelete}
                          className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg transition-colors"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="logo-upload"
                          className={`flex flex-col items-center justify-center w-full h-32 ${surface} border-2 border-dashed border-gray-700/30 rounded-xl cursor-pointer hover:border-primary-500/50 transition-colors`}
                        >
                          <i className="ri-image-add-line text-3xl text-gray-500 mb-2"></i>
                          <span
                            className={`text-sm ${themeClasses.textSecondary}`}
                          >
                            {t("yards.form.logoUpload")}
                          </span>
                          <span
                            className={`text-xs ${themeClasses.textSecondary} mt-1`}
                          >
                            {t("yards.form.logoHint")}
                          </span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    {t("yards.form.contactNumber")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <div
                      className={`flex items-center gap-1.5 px-3 py-2.5 ${surface} rounded-xl flex-shrink-0`}
                    >
                      <span className="text-base">🇲🇽</span>
                      <span className={`text-sm ${themeClasses.textSecondary}`}>
                        +52
                      </span>
                    </div>
                    <input
                      type="tel"
                      value={localForm.guardHousePhone}
                      onChange={(e) => set("guardHousePhone", e.target.value)}
                      className={`${cardCls} flex-1 ${errors.guardHousePhone ? "border-red-500/60" : ""}`}
                      placeholder={t("yards.form.contactPlaceholder")}
                    />
                  </div>
                  {errors.guardHousePhone && (
                    <span className="text-red-400 text-xs mt-1">
                      {errors.guardHousePhone}
                    </span>
                  )}
                </div>

                <div>
                  <label className={labelCls}>{t("yards.form.privacyPolicies")}</label>
                  <textarea
                    value={localForm.privacyPolicies}
                    onChange={(e) => set("privacyPolicies", e.target.value)}
                    rows={3}
                    className={cardCls}
                    placeholder={t("yards.form.privacyPlaceholder")}
                  />
                </div>

              </div>

              {/* Right column - Map */}
              <div className="lg:col-span-3 flex flex-col space-y-4 min-h-0">
                <APIProvider
                  apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  libraries={["places"]}
                >
                    {/* Location Search Input */}
                    <div className="flex-shrink-0">
                      <label className={labelCls}>{t("yards.form.searchLocation")}</label>
                      <div className="flex gap-2">
                        <LocationSearchInput
                          value={localForm.location}
                          onChange={(address, location) => {
                            set("location", address);
                            if (location) {
                              set("latitude", location.lat);
                              set("longitude", location.lng);
                              setMapCenter({
                                lat: location.lat,
                                lng: location.lng,
                              });
                            }
                          }}
                          onLocationSelect={(location) => {
                            setMapCenter({
                              lat: location.lat,
                              lng: location.lng,
                            });
                          }}
                          placeholder={t("yards.form.searchLocationPlaceholder")}
                          className={`${cardCls} ${errors.location ? "border-red-500/60" : ""} flex-1`}
                          error={errors.location}
                        />
                        <button
                          type="button"
                          onClick={handleCenterOnMyLocation}
                          className={`px-4 py-2.5 ${surface} border ${themeClasses.cardBorder} rounded-xl ${themeClasses.text} hover:bg-gray-600/20 transition-colors flex items-center gap-2`}
                          title="Centrar en mi ubicación"
                        >
                          <i className="ri-focus-3-line"></i>
                          <span className="hidden sm:inline text-sm">
                            Mi ubicación
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Map */}
                    <div
                      className={`rounded-xl overflow-hidden border ${themeClasses.cardBorder} relative flex-1 min-h-[250px] max-h-[400px]`}
                    >
                      <Map
                        defaultCenter={mapCenter}
                        defaultZoom={16}
                        mapId="truck-yard-map"
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                        zoomControl={true}
                        mapTypeId="roadmap"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <MapController center={mapCenter} />
                        <Marker
                          position={mapCenter}
                          draggable={true}
                          onDrag={handleMarkerDrag}
                        />
                        <Circle
                          center={mapCenter}
                          radius={mapRadius}
                          strokeColor={isDarkMode ? "#10b981" : "#059669"}
                          strokeOpacity={0.8}
                          strokeWeight={2}
                          fillColor={isDarkMode ? "#10b981" : "#059669"}
                          fillOpacity={0.1}
                          editable={true}
                          draggable={false}
                          onRadiusChanged={(radius: number) =>
                            handleRadiusChange(radius)
                          }
                        />
                      </Map>
                    </div>
                  </APIProvider>

                {/* Radius slider — mirrors the geofences circular form. Stays
                    in sync with the draggable circle on the map. */}
                <div>
                  <div className="w-full flex items-center gap-3">
                    <label className={labelCls}>{t("yards.form.radius")}</label>
                    <span className={`text-sm font-medium ${themeClasses.text}`}>
                      {mapRadius} m
                    </span>
                  </div>
                  <input
                    type="range"
                    min={RADIUS_MIN}
                    max={RADIUS_MAX}
                    value={Math.min(Math.max(mapRadius, RADIUS_MIN), RADIUS_MAX)}
                    onChange={(e) =>
                      handleRadiusChange(parseInt(e.target.value) || RADIUS_MIN)
                    }
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
                  />
                  <div className="w-full flex justify-between text-xs text-gray-400 mt-1">
                    <span>{RADIUS_MIN} m</span>
                    <span>{RADIUS_MAX} m</span>
                  </div>
                </div>

                {/* Timezone and Status - below map */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className={labelCls}>
                      {t("yards.form.timezone")} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={localForm.timeZone}
                      onChange={(e) => set("timeZone", e.target.value)}
                      className={`${cardCls} ${errors.timeZone ? "border-red-500/60" : ""}`}
                    >
                      <option value="">{t("yards.form.selectTimezone")}</option>
                      {TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                    {errors.timeZone && (
                      <span className="text-red-400 text-xs mt-1">
                        {errors.timeZone}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className={labelCls}>{t("yards.form.status")}</label>
                    <div className="flex gap-4">
                      {["active", "inactive"].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => set("status", s)}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl border transition-all text-sm ${
                            localForm.status === s
                              ? "border-green-500/40 bg-green-500/10 text-green-400"
                              : `${surface} border-gray-700/30 ${themeClasses.textSecondary}`
                          }`}
                        >
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${localForm.status === s ? "border-green-400" : "border-gray-500"}`}
                          >
                            {localForm.status === s && (
                              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                            )}
                          </div>
                          <span>{s === "active" ? t("yards.form.active") : t("yards.form.inactive")}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Geofence config — full width below the main grid */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className={`text-sm font-semibold ${themeClasses.text} mb-3`}>
                {t("yards.form.geofenceConfig")}
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Eventos al cruzar */}
                <div>
                  <label className={labelCls}>{t("yards.form.crossingEvents")}</label>
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1.5`}
                  >
                    {t("yards.form.crossingEventsHint")}
                  </p>
                  <div className="flex gap-3">
                    {[
                      {
                        key: "entry",
                        icon: "ri-login-circle-line",
                        label: t("yards.form.entry"),
                      },
                      {
                        key: "exit",
                        icon: "ri-logout-circle-line",
                        label: t("yards.form.exit"),
                      },
                    ].map(({ key, icon, label }) => {
                      const active = (localForm.geofenceActions || []).includes(
                        key,
                      );
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            const cur = localForm.geofenceActions || [];
                            set(
                              "geofenceActions",
                              cur.includes(key)
                                ? cur.filter((a) => a !== key)
                                : [...cur, key],
                            );
                          }}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl border transition-all text-sm ${
                            active
                              ? "border-green-500/40 bg-green-500/10 text-green-400"
                              : `${surface} border-gray-700/30 ${themeClasses.textSecondary}`
                          }`}
                        >
                          <i className={icon}></i>
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Usuarios a notificar */}
                <div>
                  <label className={labelCls}>{t("yards.form.notifiedUsers")}</label>
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1.5`}
                  >
                    {t("yards.form.notifiedUsersHint")}
                  </p>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    blurInputOnSelect={false}
                    hideSelectedOptions={false}
                    isLoading={usersLoading}
                    loadingMessage={() => t("yards.form.loadingUsers")}
                    options={userOptions}
                    styles={getCustomSelectStyles(isDarkMode)}
                    placeholder={
                      usersLoading
                        ? t("yards.form.loadingUsers")
                        : t("yards.form.selectUsers")
                    }
                    value={(localForm.notifiedUsers || []).map((val) => {
                      const existing = userOptions.find((o) => o.value === val);
                      return existing || { value: val, label: val };
                    })}
                    onChange={(selected) =>
                      set(
                        "notifiedUsers",
                        selected
                          ? (selected as { value: string }[]).map((s) => s.value)
                          : [],
                      )
                    }
                    isClearable
                    isSearchable
                    noOptionsMessage={() => t("yards.form.noUsers")}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                {/* Dispositivos */}
                <div>
                  <label className={labelCls}>{t("yards.form.devices")}</label>
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1.5`}
                  >
                    {t("yards.form.devicesHint")}
                  </p>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    blurInputOnSelect={false}
                    hideSelectedOptions={false}
                    isLoading={devicesLoading}
                    loadingMessage={() => t("yards.form.loadingDevices")}
                    options={deviceOptions}
                    styles={getCustomSelectStyles(isDarkMode)}
                    placeholder={
                      devicesLoading
                        ? t("yards.form.loadingDevices")
                        : t("yards.form.selectDevices")
                    }
                    value={(localForm.devices || []).map((val) => {
                      const existing = deviceOptions.find(
                        (o) => o.value === val,
                      );
                      return existing || { value: val, label: val };
                    })}
                    onChange={(selected) =>
                      set(
                        "devices",
                        selected
                          ? (selected as { value: string }[]).map((s) => s.value)
                          : [],
                      )
                    }
                    isClearable
                    isSearchable
                    noOptionsMessage={() => t("yards.form.noDevices")}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>

              {/* Enviar correos — texto a la izquierda, switch a la derecha */}
              <div className="mt-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <span className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("yards.form.sendEmails")}
                  </span>
                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t("yards.form.sendEmailsHint")}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={localForm.sendEmails === true}
                  onClick={() => set("sendEmails", !localForm.sendEmails)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    localForm.sendEmails ? "bg-green-500" : "bg-gray-400/50"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      localForm.sendEmails ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Tags especiales — ancho completo, debajo de la geocerca */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className={`text-sm font-semibold ${themeClasses.text} mb-1`}>
                <i className="ri-price-tag-3-line mr-1"></i>
                {t("yards.specialTags.title")}
              </h3>
              <p
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mb-3`}
              >
                {t("yards.specialTags.hint")}
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {specialTagSelectors.map(({ kind, options }) => {
                  const value = specialTags[kind] || "";
                  const selected = options.find((o) => o.value === value);
                  const selectId = `yard-special-tag-${kind}`;
                  return (
                    <div key={kind}>
                      <label htmlFor={selectId} className={labelCls}>
                        {kind === "provider"
                          ? t("yards.specialTags.provider")
                          : t("yards.specialTags.untagged")}
                      </label>
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} -mt-1 mb-1.5`}
                      >
                        {kind === "provider"
                          ? t("yards.specialTags.providerHint")
                          : t("yards.specialTags.untaggedHint")}
                      </p>
                      <select
                        id={selectId}
                        value={value}
                        onChange={(e) => setSpecialTag(kind, e.target.value)}
                        disabled={specialTagsLocked}
                        className={`${cardCls} font-mono ${specialTagsError ? "border-red-500/60" : ""} ${
                          specialTagsLocked ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                      >
                        <option value="">
                          {t("yards.specialTags.unassigned")}
                        </option>
                        {options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.cardLabel ? `${o.value} · ${o.cardLabel}` : o.value}
                            {o.unavailable
                              ? ` — ${t("yards.specialTags.unavailable")}`
                              : ""}
                          </option>
                        ))}
                      </select>
                      {/* El tag guardado dejó de ser elegible (se asignó a una
                          unidad, se dio de baja, cambió de patio). Mientras no
                          se toque sigue guardado; se avisa porque sus lecturas
                          ya no se comportan como tag especial de forma fiable. */}
                      {canEditSpecialTags &&
                        tagCardsState === "ready" &&
                        selected?.unavailable && (
                          <span className="text-yellow-500 text-xs mt-1 block">
                            <i className="ri-error-warning-line mr-1"></i>
                            {t("yards.specialTags.unavailableHint")}
                          </span>
                        )}
                    </div>
                  );
                })}
              </div>
              {specialTagsError && (
                <span className="text-red-400 text-xs mt-2 block">
                  {specialTagsError}
                </span>
              )}
              {!canEditSpecialTags ? (
                <span
                  className={`text-[11px] ${themeClasses.textSecondary} mt-2 inline-flex items-center gap-1`}
                >
                  <i className="ri-lock-line"></i>
                  {t("yards.specialTags.readOnlyHint")}
                </span>
              ) : tagCardsState === "loading" ? (
                <span
                  className={`text-[11px] ${themeClasses.textSecondary} mt-2 inline-flex items-center gap-1`}
                >
                  <i className="ri-loader-4-line animate-spin"></i>
                  {t("yards.specialTags.loading")}
                </span>
              ) : tagCardsState === "error" ? (
                <span className="text-yellow-500 text-[11px] mt-2 inline-flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {t("yards.specialTags.loadError")}
                </span>
              ) : null}
            </div>

            {/* Map controls overlay */}
            <div className="lg:hidden space-y-4 mt-6">
              <div>
                <label className={labelCls}>
                  {t("yards.form.address")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={localForm.location}
                  onChange={(e) => set("location", e.target.value)}
                  className={`${cardCls} ${errors.location ? "border-red-500/60" : ""}`}
                  placeholder={t("yards.form.addressPlaceholder")}
                />
                {errors.location && (
                  <span className="text-red-400 text-xs mt-1">
                    {errors.location}
                  </span>
                )}
                <p
                  className={`text-xs ${themeClasses.textSecondary} mt-1.5 leading-relaxed`}
                >
                  {t("yards.form.dragMarker")}
                </p>
              </div>

              <div>
                <label className={labelCls}>{t("yards.form.geofenceRadius")}</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={localForm.radius}
                    onChange={(e) =>
                      handleRadiusChange(parseInt(e.target.value) || RADIUS_MIN)
                    }
                    className={`${cardCls} w-32`}
                    placeholder={t("yards.form.radiusPlaceholder")}
                    min={RADIUS_MIN}
                    max={RADIUS_MAX}
                  />
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    {t("yards.form.metersHint")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer with buttons */}
          <div
            className={`flex justify-end gap-3 py-2 border-t ${themeClasses.cardBorder} px-4 sm:px-10 flex-shrink-0`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`px-5 py-2.5 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-xl ${themeClasses.buttonText} font-medium text-sm transition-colors`}
            >
              {t("yards.form.cancel")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-xl font-medium text-sm transition-colors flex items-center gap-2"
              style={{ backgroundColor: brand.primary, color: brand.accent }}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {isEdit ? t("yards.form.updating") : t("yards.form.creating")}
                </>
              ) : isEdit ? (
                t("yards.form.update")
              ) : (
                t("yards.form.create")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function YardsPagination({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <PaginationControls
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
    />
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({
  themeClasses,
  isDarkMode,
  onAdd,
}: {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onAdd: () => void;
}) {
  const { t } = useTranslation("truckYard");
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div
        className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
          isDarkMode ? "bg-dark-card" : "bg-gray-100"
        }`}
      >
        <i
          className={`ri-building-4-line text-4xl ${themeClasses.textSecondary}`}
        ></i>
      </div>
      <h3 className={`text-lg font-medium ${themeClasses.text} mb-2`}>
        {t("yards.empty.title")}
      </h3>
      <p className={`text-sm ${themeClasses.textSecondary} mb-4`}>
        {t("yards.empty.subtitle")}
      </p>
      <button
        onClick={onAdd}
        className="px-4 py-2 rounded-lg font-medium text-sm transition-colors"
        style={{ backgroundColor: brand.primary, color: brand.accent }}
      >
        <i className="ri-add-line mr-1"></i>
        {t("yards.empty.action")}
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

export default function YardsSection() {
  const { t } = useTranslation("truckYard");
  const { isDarkMode } = useDarkMode();
  const { yards, loading, fetchYards, createYard, updateYard, deleteYard } =
    useTruckYardStore();
  const {
    isAdmin,
    userRoleId,
    getAssignedYardIds,
    hasYardRestrictions,
    hasNoAssignedYards,
  } = useTruckYardRole();
  // El backend responde 403 si un vigilante, inspector o supervisor cambia los
  // tags especiales; el formulario se los muestra en sólo lectura.
  const canEditSpecialTags = canChangeYardSpecialTags(userRoleId);
  const queryClient = useQueryClient();

  // El badge de patios usa TanStack (qk.truckYardCounts) y no se entera de los
  // CRUD del store zustand; invalidarlo refresca el conteo al crear/borrar.
  const invalidateYardCounts = () =>
    queryClient.invalidateQueries({ queryKey: ["truckYardCounts"] });

  // Admins can only view and edit, not create or delete.
  //
  // Y tampoco crea quien está recortado por patio (issue #497): el alta no
  // agrega el id del patio nuevo a los `yard_ids` del creador, así que con el
  // filtro ahora estricto el supervisor creaba un patio que desaparecía de su
  // propia lista al instante — sin poder editarlo ni borrarlo. Antes quedaba
  // visible sólo por el escape del fallo abierto, o sea que "funcionaba" por el
  // mismo defecto que este PR cierra.
  //
  // Si operaciones quiere que el supervisor pueda crear patios, el cambio no es
  // devolverle el botón: es que el alta lo agregue a su propia asignación
  // (backend), y ahí este permiso vuelve solo.
  const canCreateDelete = !isAdmin && !hasYardRestrictions();

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);

  // Modals
  const [detailsYard, setDetailsYard] = useState<Yard | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editYard, setEditYard] = useState<Yard | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchYards();
  }, [fetchYards]);

  // Deep-link from the Geofences panel ("Ir a patios"): ?open=<yardId>.
  // Once yards are loaded, open that yard's details modal and clear the param.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || yards.length === 0) return;
    const yard = yards.find((y) => y.id === openId);
    if (yard) {
      setDetailsYard(yard as Yard);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, yards, setSearchParams]);

  // ── Label catalogs for the details modal ──────────────────────────────
  // Devices (company-scoped) to show device names; users fetched across all
  // companies so notified users from BOTH the reseller and carriers resolve
  // to a readable name (display only — does not affect the form's selection).
  const deviceCatalog = useAllDevices().data ?? [];

  const [userLabelById, setUserLabelById] = useState<Record<string, string>>(
    {},
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/userList/light", {
          _skipCompanyScope: true,
        } as Parameters<typeof api.get>[1]);
        if (cancelled) return;
        const map: Record<string, string> = {};
        (data.data || []).forEach((u: any) => {
          const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
          map[u.id] = u.email ? `${name} (${u.email})` : name || u.id;
        });
        setUserLabelById(map);
      } catch {
        /* labels fall back to the raw id/email */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const deviceLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    deviceCatalog.forEach((d) => {
      const vehicleName = d.vehicle ? d.vehicle?.name : "";
      const trailerName = d.remolque ? d.remolque?.name : "";
      const nameDevice = vehicleName || trailerName ? d.identifier : d.name;
      map[d.id] =
        `${nameDevice}${vehicleName ? ` (Vehículo: ${vehicleName})` : ""}${trailerName ? ` (Remolque: ${trailerName})` : ""}`;
    });
    return map;
  }, [deviceCatalog]);

  // Stable initial-form value for the modal. Memoized on `editYard` so its
  // reference only changes when a different yard is opened — otherwise a
  // parent re-render (e.g. the store update after saving) would recreate it
  // and the modal's `useEffect([form])` would reset the user's edits.
  const yardFormValue = useMemo<YardFormData>(
    () =>
      editYard
        ? {
            name: editYard.name,
            location: editYard.location,
            latitude: editYard.latitude,
            longitude: editYard.longitude,
            radius: String(editYard.radius),
            guardHousePhone: editYard.guardHousePhone,
            timeZone: editYard.timeZone,
            privacyPolicies: editYard.privacyPolicies,
            status: editYard.status,
            type: editYard.type,
            logo: editYard.logo,
            notifiedUsers: editYard.notifiedUsers || [],
            devices: editYard.devices || [],
            geofenceActions: editYard.geofenceActions || ["entry", "exit"],
            sendEmails: editYard.sendEmails ?? false,
            special_tags: normalizeSpecialTags(editYard.special_tags),
          }
        : EMPTY_FORM,
    [editYard],
  );

  // Get assigned yard IDs for supervisors
  const assignedYardIds = getAssignedYardIds();

  // Filtered yards (apply role-based restrictions first)
  const filtered = yards.filter((y) => {
    // Role-based filter: supervisors only see their assigned yards.
    // Sin el `&& assignedYardIds.length > 0` que había acá: en un rol
    // restringido la lista vacía significa "sin patios asignados", no "sin
    // restricción" (issue #497) — antes apagaba el recorte y mostraba todos.
    if (hasYardRestrictions()) {
      if (!assignedYardIds.includes(y.id)) return false;
    }

    const matchSearch =
      !searchQuery ||
      y.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      y.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      y.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = !selectedStatus || y.status === selectedStatus;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  // Conteos de operación del día, uno por patio VISIBLE (`paginated`, no
  // `filtered`): `/yardMetrics` acepta un solo yard_id por request, así que el
  // costo es N y la única forma de acotarlo es no pedir lo que no se pinta. Con
  // PAGE_SIZE = 10 el techo son 10 requests cada 30 s; medido el 4-sep-2026 hay
  // 7 patios en producción y 8 en dev, o sea que hoy son 7. El porqué completo
  // —y cuándo esto deja de alcanzar— está en useYardMetrics.ts.
  //
  // Se pausa con cualquier modal abierta, igual que el polling de accesos
  // (accessSection/page.tsx:2667): mientras alguien lee el detalle o edita el
  // formulario, refrescar por debajo no le sirve a nadie y compite con la
  // pantalla que sí está usando.
  // Con una modal abierta se apaga el REFRESCO, no la primera carga: el enlace
  // profundo del panel de geocercas (`?open=<yardId>`) abre la modal de
  // detalles antes de que haya llegado una sola respuesta, y apagar la consulta
  // entera dejaba las tarjetas de atrás en "Cargando conteos…" sin que nada
  // estuviera cargando.
  const polling = !detailsYard && !formOpen;

  const yardMetrics = useYardMetrics(paginated, { polling });

  // El listado también se refresca, porque `yard.health` viaja adentro de él:
  // sin esto el punto de color quedaría congelado en el estado que tenía cuando
  // se abrió la pestaña.
  useYardsPoll({ polling });

  const handleOpenAdd = () => {
    setEditYard(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (yard: Yard) => {
    setDetailsYard(null);
    setEditYard(yard);
    setFormOpen(true);
  };

  const handleSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    try {
      if (editYard) {
        const updated = await updateYard(editYard.id, formData);
        if (!updated) throw new Error("updateYard failed");
        Swal.fire({
          icon: "success",
          title: t("yards.messages.updatedTitle"),
          text: t("yards.messages.updatedText"),
          confirmButtonColor: brand.primary,
        });
      } else {
        const created = await createYard(formData);
        if (!created) throw new Error("createYard failed");
        Swal.fire({
          icon: "success",
          title: t("yards.messages.createdTitle"),
          text: t("yards.messages.createdText"),
          confirmButtonColor: brand.primary,
        });
      }
      setFormOpen(false);
      setEditYard(null);
      await fetchYards();
      invalidateYardCounts();
    } catch (error) {
      // `createYard` / `updateYard` no relanzan: dejan el texto del backend en
      // `error` del store. Si es uno de los códigos de tags especiales se
      // muestra su explicación; cualquier otro conserva el mensaje genérico.
      const tagErrorKey = specialTagErrorKey(
        useTruckYardStore.getState().error,
      );
      Swal.fire({
        icon: "error",
        title: t("yards.messages.errorTitle"),
        text: tagErrorKey ? t(tagErrorKey) : t("yards.messages.errorText"),
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (yard: Yard) => {
    const result = await Swal.fire({
      title: t("yards.messages.deleteTitle"),
      text: t("yards.messages.deleteText", { name: yard.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: brand.primary,
      confirmButtonText: t("yards.messages.deleteConfirm"),
      cancelButtonText: t("yards.messages.deleteCancel"),
    });

    if (result.isConfirmed) {
      setIsDeleting(true);
      try {
        const success = await deleteYard(yard.id);
        if (success) {
          await fetchYards();
          invalidateYardCounts();
          Swal.fire({
            title: t("yards.messages.deletedTitle"),
            text: t("yards.messages.deletedText"),
            icon: "success",
            confirmButtonColor: brand.primary,
          });
        } else {
          throw new Error("No se pudo eliminar el patio");
        }
      } catch (error) {
        Swal.fire({
          title: t("yards.messages.errorTitle"),
          text: t("yards.messages.deleteErrorText"),
          icon: "error",
          confirmButtonColor: "#ef4444",
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return (
    <div className={`h-full flex flex-col relative`}>
      {/* Header */}
      <HeaderComponent
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        title={t("yards.title")}
        subtitle={t("yards.subtitle")}
        searchQuery={searchQuery}
        selectedStatus={selectedStatus}
        selectedView={selectedView}
        onSearchChange={(v: string) => {
          setSearchQuery(v);
          setCurrentPage(1);
        }}
        onStatusChange={(v: string) => {
          setSelectedStatus(v);
          setCurrentPage(1);
        }}
        onViewChange={setSelectedView}
        onAdd={handleOpenAdd}
        addButtonText="Nuevo Patio"
        addButtonShortText="Nuevo"
        searchPlaceholder="Buscar patio..."
        showAddButton={canCreateDelete}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {hasNoAssignedYards() && (
          <div className="mb-4">
            <NoAssignedYardsNotice isDarkMode={isDarkMode} what="ningún patio" />
          </div>
        )}
        {loading ? (
          <div
            className={`py-16 text-center text-sm ${themeClasses.textSecondary}`}
          >
            {t("yards.loading")}
          </div>
        ) : paginated.length === 0 ? (
          <EmptyState
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onAdd={handleOpenAdd}
          />
        ) : selectedView === "list" ? (
          <div className="space-y-3">
            {paginated.map((yard) => (
              <YardListRow
                key={yard.id}
                yard={yard}
                metrics={yardMetrics[yard.id]}
                themeClasses={themeClasses}
                onDetails={setDetailsYard}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
                canDelete={canCreateDelete}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginated.map((yard) => (
              <YardGridCard
                key={yard.id}
                yard={yard}
                metrics={yardMetrics[yard.id]}
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                onDetails={setDetailsYard}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
                canDelete={canCreateDelete}
              />
            ))}
          </div>
        )}

        <YardsPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Details Modal */}
      {detailsYard && (
        <YardDetailsModal
          yard={detailsYard}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          isDeleting={isDeleting}
          userLabelById={userLabelById}
          deviceLabelById={deviceLabelById}
          onClose={() => setDetailsYard(null)}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
          canDelete={canCreateDelete}
        />
      )}

      {/* Form Modal */}
      {formOpen && (
        <YardFormModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          isEdit={!!editYard}
          isSubmitting={isSubmitting}
          form={yardFormValue}
          yardId={editYard?.id ?? null}
          canEditSpecialTags={canEditSpecialTags}
          onClose={() => {
            setFormOpen(false);
            setEditYard(null);
          }}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
